import { FC, ReactElement, ReactNode, useCallback, useState } from "react";
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
} from "@mui/material";
import { CiSquarePlus } from "react-icons/ci";
import { FaSquareMinus } from "react-icons/fa6";

import { COUNTRIES } from "@/constants";
import { getCountryFlag } from "@/constants/country-flags";
import { HTSCodeWithTooltip } from "@/components";

interface IRow {
  country: CountryData;
  htsData: (IHtsNumber & { exportCountry: string })[];
  columns: IColumn<CountryData>[];
}

const Row: FC<IRow> = ({ country, htsData, columns }): ReactElement => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const renderRow = useCallback(() => {
    return (
      <TableRow
        sx={{
          ...(isExpanded && {
            background: "#f5f5f5",
            borderBottom: 0,
          }),
        }}
      >
        {columns.map((column) => {
          return (
            <TableCell
              key={`${column.key}-${country._id}`}
              sx={{
                width: column.width,
                minWidth: column.minWidth || "auto",
                padding: { xs: "3px 4px", md: "5px 6px" },
                fontSize: { xs: "9px", md: "10px", lg: "11px" },
                border: "0 !important",
                borderBottom: isExpanded ? "0 !important" : "1px solid #E6E7E7 !important",
              }}
            >
              {column?.key === "_id" ? (
                <Stack direction="row" gap={1.25} alignItems="center">
                  <IconButton
                    aria-label="expand row"
                    size="small"
                    onClick={() => setIsExpanded(!isExpanded)}
                  >
                    {isExpanded ? (
                      <FaSquareMinus fontSize={20} color="#0B494B" />
                    ) : (
                      <CiSquarePlus fontSize={20} color="#E1E1E1" />
                    )}
                  </IconButton>
                  {COUNTRIES.find(
                    (singleCountry) => singleCountry.shortForm === country._id
                  )?.name ? (
                    <span style={{ fontSize: "20px", lineHeight: 1 }}>
                      {getCountryFlag(country._id)}
                    </span>
                  ) : null}
                  <Typography 
                    variant="body2" 
                    whiteSpace="nowrap"
                    sx={{ fontSize: { xs: "9px", md: "10px", lg: "11px" } }}
                  >
                    {COUNTRIES.find(
                      (singleCountry) => singleCountry.shortForm === country._id
                    )?.name ?? country._id}
                  </Typography>
                </Stack>
              ) : (
                column?.component?.(country) ??
                country[column.key as keyof CountryData]
              )}
            </TableCell>
          );
        })}
      </TableRow>
    );
  }, [isExpanded, columns, country]);

  // Create columns for HTS detail rows - first column is export country, second is HTS code
  const htsColumns: IColumn<IHtsNumber & { exportCountry: string }>[] = columns.map((col, index) => {
    if (index === 0) {
      // First column - Country of Export
      return {
        name: col.name,
        key: "exportCountry" as any,
        align: "left",
        width: col.width,
        component: (data) => {
          const exportCountry = data.exportCountry;
          const country = COUNTRIES.find(c => c.shortForm === exportCountry);
          return (
            <Stack direction="row" gap={1} alignItems="center" pl={5.5}>
              {country?.name && (
                <span style={{ fontSize: "20px", lineHeight: 1 }}>
                  {getCountryFlag(exportCountry)}
                </span>
              )}
              <Typography variant="body2" fontSize={13}>
                {country?.name || exportCountry}
              </Typography>
            </Stack>
          );
        },
      };
    } else if (index === 1) {
      // Second column - HTS Code
      return {
        name: col.name,
        key: "id" as any,
        align: col.align,
        width: col.width,
        component: (data) => {
          return (
            <HTSCodeWithTooltip htsCode={data.id.toString()}>
              <Typography 
                variant="body2" 
                fontSize={13}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    color: '#0b494b',
                    textDecoration: 'underline',
                  },
                }}
              >
                {data.id}
              </Typography>
            </HTSCodeWithTooltip>
          );
        },
      };
    } else {
      // All other columns remain the same
      const newCol = { ...col };
      return newCol as IColumn<IHtsNumber & { exportCountry: string }>;
    }
  });

  return (
    <>
      {!isExpanded && renderRow()}
      <TableRow
        sx={{
          borderBottom: 0,
        }}
      >
        <TableCell
          style={{ padding: 0, ...(!isExpanded && { border: "0 !important" }) }}
          colSpan={columns.length}
        >
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box
              sx={{
                borderRadius: "4px",
                border: "1px solid #E0E0E0",
                overflow: "hidden",
                backgroundColor: "#fafafa",
              }}
            >
              <Table aria-label="purchases">
                <TableBody>
                  {isExpanded ? renderRow() : null}
                  {htsData.map((hts, index) => (
                    <TableRow
                      key={`${hts.id}-${index}`}
                      sx={{
                        background: "#ffffff",
                        "&:hover": {
                          background: "#f5f5f5",
                        },
                      }}
                    >
                      {htsColumns.map((column) => {
                        return (
                          <TableCell
                            sx={{
                              border: "0 !important",
                              borderBottom: "1px solid #E0E0E0 !important",
                              lineHeight: "20px",
                              width: column.width,
                            }}
                            key={`${column.key}-${hts.id}`}
                          >
                            {column.component ? (
                              column.component(hts)
                            ) : (
                              <>{hts[column.key as keyof (IHtsNumber & { exportCountry: string })]}</>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

interface ICollapsibleTable {
  countries: CountryData[];
  htsData: Record<string, (IHtsNumber & { exportCountry: string })[]>;
  columns: IColumn<CountryData>[];
  footer?: () => ReactNode;
}

const CollapsibleTable: FC<ICollapsibleTable> = ({
  countries,
  htsData,
  columns,
  footer,
}): ReactElement => {
  return (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight: { xs: 360, md: 400 },
        borderRadius: "10px 10px 8px 8px",
        borderBlock: "1px solid rgba(5, 34, 35, 0.12)",
        boxShadow: "none",
        "&::-webkit-scrollbar": {
          width: 4,
          height: 4,
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#555",
        },
      }}
    >
      <Table
        stickyHeader
        aria-label="sticky table"
        sx={{
          borderCollapse: "collapse",
          tableLayout: "fixed",
          width: "100%",
          minWidth: { xs: "600px", md: "100%" },
          "& .MuiTableCell-root": {
            border: "0 !important",
          },
          "& .MuiTableBody-root .MuiTableCell-root": {
            borderTop: "1px solid #E6E7E7 !important",
            borderBottom: "1px solid #E6E7E7 !important",
          },
        }}
      >
        <TableHead>
          <TableRow>
            {columns.map((column) => {
              return (
                <TableCell
                  key={`head-${column.key}`}
                  align={column.align}
                  sx={{
                    width: column.width,
                    minWidth: column.minWidth || "auto",
                    backgroundColor: "#E6ECED !important",
                    color: "#030712 !important",
                    border: "0 !important",
                    padding: { xs: "3px 4px", md: "5px 6px" },
                  }}
                >
                  <Tooltip title={column.name} arrow placement="top">
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        width: "100%",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontSize: { xs: "9px", md: "10px", lg: "11px" },
                          fontWeight: 500,
                          lineHeight: 1.2,
                          color: "inherit",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {column.name}
                      </Typography>
                    </Box>
                  </Tooltip>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {countries.map((country) => {
            return (
              <Row
                country={country}
                htsData={htsData[country._id] || []}
                columns={columns}
                key={country._id}
              />
            );
          })}
          {footer?.()}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CollapsibleTable;